// assistantPill — the minimised chat panel's state (pen K-08), projected from
// the chat module's state for the conversation the panel holds. Pure: no
// clock, no memory of earlier renders.
//
// Precedence: a running (or held) turn is `working`; a refused request is
// `interrupted`; then the current turn — everything after the last author
// message — decides: a waiting form is `needs_you`, a closing system row or a
// stopped reply is `interrupted`, a reply that landed in this session is
// `done`. Anything else, a fresh session included, is `idle`.

import * as TurnLiveness from "@bilda/app/turnLiveness";
import type {
    AssistantPillProgress,
    AssistantPillState,
    AssistantPillStep,
} from "../components/AssistantPill/types.js";
import type {
    ChatEntry,
    MaterialBuildingEntry,
    SystemEntry,
} from "../components/Chat/types.js";
import type { ChatSession } from "../modules/chat/model.js";
import { questionCounts } from "./materialBuildModel.js";

export { assistantPillState };

const IDLE: AssistantPillState = { type: "idle" };

// The entries of the current turn: those after the last author message (all
// of them before the first).
const currentTurn = (
    conversation: readonly ChatEntry[],
): readonly ChatEntry[] =>
    conversation.slice(
        conversation.reduce(
            (from, e, i) => (e.type === "author" ? i + 1 : from),
            0,
        ),
    );

const isBuilding = (e: ChatEntry): e is MaterialBuildingEntry =>
    e.type === "material_building";
const isSystem = (e: ChatEntry): e is SystemEntry => e.type === "system";

// "N of M" from the turn's live material build, when it has planned slots.
const buildProgress = (
    turn: readonly ChatEntry[],
): AssistantPillProgress | undefined => {
    const building = turn.filter(isBuilding).at(-1);
    if (building === undefined) return undefined;
    const { filled, total } = questionCounts(building.build);
    return total > 0 ? { done: filled, total } : undefined;
};

// Entries a landed reply leaves behind.
const isOutput = (e: ChatEntry): boolean =>
    e.type === "agent" ||
    e.type === "material" ||
    e.type === "export" ||
    e.type === "proposal" ||
    e.type === "plan" ||
    e.type === "information_request";

const working = (
    step: AssistantPillStep,
    progress: AssistantPillProgress | undefined,
): AssistantPillState =>
    progress === undefined
        ? { type: "working", step }
        : { type: "working", step, progress };

const assistantPillState = (
    session: ChatSession,
    liveness: TurnLiveness.State,
    conversationId: string | null,
): AssistantPillState => {
    if (conversationId === null) return IDLE;
    const turn = currentTurn(session.conversation);
    if (session.drafting)
        return working(
            {
                type: "live",
                activity: session.liveActivity ?? { type: "thinking" },
            },
            buildProgress(turn),
        );
    if (session.heldSend.type !== "no_held_send")
        return working({ type: "sending" }, undefined);
    const { turnError } = session;
    if (turnError !== null)
        return {
            type: "interrupted",
            cause:
                turnError.type === "port_refused"
                    ? { type: "failed", error: turnError.error }
                    : { type: "failed" },
        };
    if (
        turn.some(
            (e) =>
                e.type === "information_request" && e.status.type === "pending",
        )
    )
        return { type: "needs_you" };
    const closing = turn.filter(isSystem).at(-1);
    if (closing !== undefined)
        return { type: "interrupted", cause: closing.kind };
    // Settled in this session (the liveness fact is absent until a turn ran).
    if (liveness.status.get(conversationId) !== "idle") return IDLE;
    const stopped = turn.some(
        (e) => e.type === "agent" && TurnLiveness.isFenced(liveness, e.id),
    );
    return stopped || !turn.some(isOutput)
        ? { type: "interrupted", cause: { type: "interrupted" } }
        : { type: "done" };
};
